import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import Swal from "sweetalert2";
import Footer from "../../../core/common/footer/Footer";
import { RootState } from "../../../core/data/redux/store";
import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
import { all_routes } from "../../router/all_routes";
import { useAuth } from "../../auth/AuthContext";
import useApi from "../../../core/customhook/useApi";
import { apibaseurl } from "../../../environment";
import { disabledStatuses } from "../../../core/common/DateValidHelper";

const QUALIFICATIONS = [
  { value: "post_graduation", label: "Post Graduation / Master's Degree" },
  { value: "graduation", label: "Bachelor's Degree" },
  {
    value: "diploma_professional_certification",
    label: "Diploma / Professional Certification",
  },
  { value: "12th_standard", label: "12th Standard" },
  { value: "10th_standard", label: "10th Standard" },
] as const;

type DynamicField = {
  field_id: string;
  field_uuid?: string;
  label: string;
  type: string;
  required?: boolean;
  value?: any;
  placeholder?: string;
  display_order?: number;
  options?: Array<string | { label?: string; value?: string; key?: string }>;
  validation_rules?: {
    minLength?: number;
    maxLength?: number;
    regex?: string;
    pattern?: string;
    readonly?: boolean;
    default?: string | number;
  };
  file_config?: {
    multiple?: boolean;
    allowedExtensions?: string[];
    allowedTypes?: string[];
  };
  uploaded_files?: Array<{
    url: string;
    name?: string;
    original_name?: string;
    id?: number;
  }>;
  form_type_new?: string;
  education_type?: string;
};
type Values = Record<string, any>;
type FileMap = Record<string, DynamicField["uploaded_files"]>;
type Entry = {
  id: number;
  education_type: string;
  fields: DynamicField[];
  values: Values;
  uploadedFiles: FileMap;
  noDocument: Record<string, boolean>;
  errors: Record<string, string>;
  loading: boolean;
  loaded: boolean;
  collapsed: boolean;
  formTypeNew?: string;
};

let nextEntryId = 1;
const emptyEntry = (): Entry => ({
  id: nextEntryId++,
  education_type: "",
  fields: [],
  values: {},
  uploadedFiles: {},
  noDocument: {},
  errors: {},
  loading: false,
  loaded: false,
  collapsed: false,
});
const fileArray = (v: any): File[] =>
  v instanceof File
    ? [v]
    : Array.isArray(v)
      ? v.filter((f): f is File => f instanceof File)
      : [];
const titleFor = (type: string) =>
  QUALIFICATIONS.find((q) => q.value === type)?.label || "Select qualification";

// Backend may return saved records as data.fields = [{education_type, fields: [...]}, ...].
// Keep each record's field metadata and uploaded documents so navigating back
// restores both qualifications without asking the user to select them again.
const restoredEntries = (response: any): Entry[] | null => {
  const raw = response?.data?.fields;
  if (!Array.isArray(raw) || !raw.length || !Array.isArray(raw[0]?.fields))
    return null;
  const entries = raw.slice(0, 2).map((item: any) => {
    const fields: DynamicField[] = item.fields || [];
    const values: Values = {};
    const uploadedFiles: FileMap = {};
    fields.forEach((field) => {
      values[field.field_id] =
        field.type === "file" ? null : (field.value ?? "");
      if (field.uploaded_files?.length)
        uploadedFiles[field.field_id] = field.uploaded_files;
    });
    const type =
      item.education_type ||
      item.form_type ||
      item.form_type_new ||
      fields.find((f: any) => f.education_type)?.education_type ||
      "";
    return {
      ...emptyEntry(),
      education_type: type,
      fields,
      values,
      uploadedFiles,
      loaded: !!type,
      formTypeNew:
        response?.data?.form_type_new ||
        item.form_type_new ||
        fields[0]?.form_type_new,
    };
  });
  return entries;
};

// One mounted child per record: its own useApi instance prevents qualification URLs
// and responses from being shared between the two education records.
const EducationCard: React.FC<{
  entry: Entry;
  index: number;
  assignmentTemplateId: string | number;
  disabled: boolean;
  usedTypes: string[];
  onPatch: (id: number, patch: Partial<Entry>) => void;
  onChange: (id: number, fieldId: string, value: any) => void;
  onChoose: (id: number, type: string) => void;
  onRemove: (id: number) => void;
  canRemove: boolean;
}> = ({
  entry,
  index,
  assignmentTemplateId,
  disabled,
  usedTypes,
  onPatch,
  onChange,
  onChoose,
  onRemove,
  canRemove,
}) => {
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const latestRequest = useRef(0);
  const [universitySearch, setUniversitySearch] = useState<{
    field: string;
    value: string;
  } | null>(null);
  const [universityOptions, setUniversityOptions] = useState<
    Array<{ name: string }>
  >([]);
  const [activeUniversity, setActiveUniversity] = useState<string | null>(null);
  const fieldAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}?education_type=${encodeURIComponent(entry.education_type)}`,
    { method: "GET", autoCall: false, handleGlobalError: false },
  );

  useEffect(() => {
    if (!entry.education_type || !assignmentTemplateId || entry.loaded) return;
    let mounted = true;
    const request = ++latestRequest.current;
    onPatch(entry.id, { loading: true, loaded: false });
    fieldAPI
      .callApi()
      .then((response: any) => {
        if (!mounted || request !== latestRequest.current) return;
        if (!response?.success)
          throw new Error(
            response?.message || "Unable to load education fields",
          );
        const raw = response?.data?.fields || [];
        const matching =
          Array.isArray(raw) && raw[0]?.fields
            ? raw.find(
                (item: any) =>
                  item.education_type === entry.education_type ||
                  item.form_type === entry.education_type,
              ) || raw[0]
            : null;
        const fields: DynamicField[] = matching ? matching.fields : raw;
        let user: any = {};
        try {
          user = JSON.parse(localStorage.getItem("userPersonalData") || "{}");
        } catch {
          user = {};
        }
        const fullname = [user.first_name, user.middle_name, user.last_name]
          .filter(Boolean)
          .join(" ");
        const values: Values = {};
        const uploadedFiles: FileMap = {};
        fields.forEach((field) => {
          values[field.field_id] =
            field.value ?? (field.type === "file" ? null : "");
          if (field.uploaded_files?.length)
            uploadedFiles[field.field_id] = field.uploaded_files;
          if (
            field.field_id.endsWith("_applicant_name") &&
            !values[field.field_id]
          )
            values[field.field_id] = fullname;
        });
        onPatch(entry.id, {
          fields,
          values,
          uploadedFiles,
          noDocument: {},
          errors: {},
          loading: false,
          loaded: true,
          formTypeNew:
            matching?.form_type_new ||
            fields[0]?.form_type_new ||
            response?.data?.form_type_new,
        });
      })
      .catch((error: any) => {
        if (!mounted || request !== latestRequest.current) return;
        onPatch(entry.id, { loading: false, loaded: false });
        Swal.fire(
          "Unable to load form",
          error?.message || "Please try selecting the qualification again.",
          "error",
        );
      });
    return () => {
      mounted = false;
    };
    // A new qualification remounts this effect; restored entries skip refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.education_type, entry.loaded, assignmentTemplateId]);

  useEffect(() => {
    if (!universitySearch?.value.trim()) {
      setUniversityOptions([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await fetch(
          `https://candidate.auptech.in/api/universities?name=${encodeURIComponent(universitySearch.value)}&country=India`,
          { signal: controller.signal },
        );
        if (!result.ok) throw new Error("University lookup failed");
        const data = await result.json();
        setUniversityOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!controller.signal.aborted) setUniversityOptions([]);
      }
    }, 600);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [universitySearch]);

  const universityFieldIds = [
    "post_graduation_university_board_name",
    "post_graduation_college_name",
    "graduation_university_board_name",
    "graduation_college_name",
    "diploma_university_board_name",
    "diploma_college_name",
  ];
  const clearError = (fieldId: string) => {
    onPatch(entry.id, { errors: { ...entry.errors, [fieldId]: "" } });
  };
  const changeField = (fieldId: string, value: any) => {
    onChange(entry.id, fieldId, value);
    if (universityFieldIds.includes(fieldId)) {
      setActiveUniversity(fieldId);
      setUniversitySearch({ field: fieldId, value: String(value) });
    } else {
      setActiveUniversity(null);
      setUniversityOptions([]);
    }
  };
  const fileFields = entry.fields
    .filter((f) => f.type === "file")
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  const normalFields = entry.fields
    .filter((f) => f.type !== "file")
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <div className="border rounded-3 mb-4 overflow-hidden bg-white">
      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom bg-light">
        <div>
          <h6 className="fw-semibold mb-1">Education {index + 1}</h6>
          <small className="text-muted">{titleFor(entry.education_type)}</small>
        </div>
        <div className="d-flex gap-2">
          {canRemove && !disabled && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onRemove(entry.id)}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => onPatch(entry.id, { collapsed: !entry.collapsed })}
          >
            <i
              className={`bi ${
                entry.collapsed ? "bi-chevron-down" : "bi-chevron-up"
              }`}
            ></i>
          </button>
        </div>
      </div>
      {!entry.collapsed && (
        <div className="p-4">
          <div className="mb-4">
            <label className="form-label fw-semibold">
              Select Qualification <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${entry.errors.education_type ? "is-invalid" : ""}`}
              value={entry.education_type}
              disabled={disabled || entry.loading}
              onChange={(e) => {
                setUniversitySearch(null);
                setUniversityOptions([]);
                onChoose(entry.id, e.target.value);
              }}
            >
              <option value="">Select qualification</option>
              {QUALIFICATIONS.map((q) => (
                <option
                  key={q.value}
                  value={q.value}
                  disabled={
                    usedTypes.includes(q.value) &&
                    entry.education_type !== q.value
                  }
                >
                  {q.label}
                </option>
              ))}
            </select>
            {entry.errors.education_type && (
              <div className="invalid-feedback d-block">
                {entry.errors.education_type}
              </div>
            )}
          </div>
          {entry.loading && (
            <div className="text-muted py-4 text-center">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading qualification fields...
            </div>
          )}
          {entry.education_type && !entry.loading && !entry.loaded && (
            <p className="text-danger">
              Fields could not be loaded. Select the qualification again to
              retry.
            </p>
          )}
          {entry.loaded && (
            <>
              <div className="row mb-3">
                {normalFields.map((field) => {
                  const value = entry.values[field.field_id] ?? "";
                  return (
                    <div
                      className="col-md-6 "
                      key={field.field_uuid || field.field_id}
                    >
                      <label className="form-label fw-medium">
                        {field.label}
                        {field.required && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </label>
                      {field.type === "dropdown" ? (
                        <select
                          className={`form-select ${entry.errors[field.field_id] ? "is-invalid" : ""}`}
                          value={String(value)}
                          disabled={disabled}
                          onChange={(e) =>
                            changeField(field.field_id, e.target.value)
                          }
                        >
                          <option value="">
                            {field.placeholder || "Select"}
                          </option>
                          {(field.options || []).map((option, i) => (
                            <option
                              key={i}
                              value={
                                typeof option === "string"
                                  ? option
                                  : (option.value ?? "")
                              }
                            >
                              {typeof option === "string"
                                ? option
                                : option.label || option.key || option.value}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="position-relative">
                          <input
                            className={`form-control ${
                              entry.errors[field.field_id] ? "is-invalid" : ""
                            }`}
                            type={field.type || "text"}
                            placeholder={field.placeholder ?? ""}
                            value={String(
                              entry.values[field.field_id] ||
                                field.validation_rules?.default ||
                                "",
                            )}
                            readOnly={field.validation_rules?.readonly === true}
                            disabled={disabled}
                            minLength={field.validation_rules?.minLength}
                            maxLength={field.validation_rules?.maxLength}
                            onChange={(e) =>
                              changeField(field.field_id, e.target.value)
                            }
                          />
                          {activeUniversity === field.field_id &&
                            universityOptions.length > 0 && (
                              <div
                                className="position-absolute w-100 bg-white border rounded shadow-sm"
                                style={{
                                  top: "100%",
                                  zIndex: 1050,
                                  maxHeight: 220,
                                  overflowY: "auto",
                                }}
                              >
                                {universityOptions.map((u, i) => (
                                  <button
                                    type="button"
                                    key={`${u.name}-${i}`}
                                    className="d-block w-100 text-start border-0 bg-white px-3 py-2"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      onChange(
                                        entry.id,
                                        field.field_id,
                                        u.name,
                                      );
                                      clearError(field.field_id);
                                      setUniversityOptions([]);
                                      setActiveUniversity(null);
                                      setUniversitySearch(null);
                                    }}
                                  >
                                    {u.name}
                                  </button>
                                ))}
                              </div>
                            )}
                        </div>
                      )}
                      {entry.errors[field.field_id] && (
                        <div className="invalid-feedback d-block">
                          {entry.errors[field.field_id]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {fileFields.length > 0 && (
                <div className="border-top mt-4 pt-4">
                  {/* Documents Header */}
                  <div className="mb-4">
                    <h6 className="fw-semibold mb-1">
                      {titleFor(entry.education_type)} Documents
                    </h6>
                    <p className="small text-muted mb-0">
                      Upload readable PDF, JPG, JPEG or PNG files. Maximum 10 MB
                      per file.
                    </p>
                  </div>

                  {fileFields.map((field) => {
                    const noDoc = !!entry.noDocument[field.field_id];

                    const selectedFiles = fileArray(
                      entry.values[field.field_id],
                    );

                    const existing = entry.uploadedFiles[field.field_id] || [];

                    const allowed = field.file_config?.allowedExtensions ||
                      field.file_config?.allowedTypes || [
                        "pdf",
                        "jpg",
                        "jpeg",
                        "png",
                      ];

                    return (
                      <div
                        key={field.field_uuid || field.field_id}
                        className="row align-items-center border-bottom py-4"
                      >
                        {/* Upload Section */}
                        <div className="col-lg-7">
                          <label className="form-label fw-semibold">
                            {field.label}
                            {field.required && (
                              <span className="text-danger ms-1">*</span>
                            )}
                          </label>

                          <input
                            ref={(el) => {
                              fileRefs.current[field.field_id] = el;
                            }}
                            type="file"
                            className={`form-control ${
                              entry.errors[field.field_id] ? "is-invalid" : ""
                            }`}
                            multiple={!!field.file_config?.multiple}
                            accept={allowed
                              .map((t: string) => `.${t.replace(/^\./, "")}`)
                              .join(",")}
                            disabled={disabled || noDoc}
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);

                              const invalid = files.find(
                                (f) => f.size > 10 * 1024 * 1024,
                              );

                              if (invalid) {
                                e.target.value = "";

                                Swal.fire(
                                  "File too large",
                                  `${invalid.name} exceeds 10 MB.`,
                                  "warning",
                                );

                                return;
                              }

                              changeField(
                                field.field_id,
                                field.file_config?.multiple
                                  ? files
                                  : files[0] || null,
                              );
                            }}
                          />

                          {/* No Document Checkbox */}
                          <div className="form-check mt-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`no-doc-${entry.id}-${field.field_id}`}
                              checked={noDoc}
                              disabled={disabled}
                              onChange={(e) => {
                                const checked = e.target.checked;

                                onPatch(entry.id, {
                                  noDocument: {
                                    ...entry.noDocument,
                                    [field.field_id]: checked,
                                  },
                                  values: checked
                                    ? {
                                        ...entry.values,
                                        [field.field_id]: null,
                                      }
                                    : entry.values,
                                  errors: {
                                    ...entry.errors,
                                    [field.field_id]: "",
                                  },
                                });

                                if (
                                  checked &&
                                  fileRefs.current[field.field_id]
                                ) {
                                  fileRefs.current[field.field_id]!.value = "";
                                }
                              }}
                            />

                            <label
                              className="form-check-label small text-muted"
                              htmlFor={`no-doc-${entry.id}-${field.field_id}`}
                            >
                              I do not have the document
                            </label>
                          </div>

                          {entry.errors[field.field_id] && (
                            <div className="invalid-feedback d-block mt-2">
                              {entry.errors[field.field_id]}
                            </div>
                          )}
                        </div>

                        {/* File Preview Section */}
                        <div className="col-lg-5 mt-3 mt-lg-0">
                          <div className="d-flex flex-column align-items-center text-center">
                            {/* Newly Selected Files */}
                            {selectedFiles.length > 0 ? (
                              <>
                                <i
                                  className="bi bi-file-earmark-pdf-fill text-danger"
                                  style={{ fontSize: "48px" }}
                                />

                                {selectedFiles.map((file, i) => (
                                  <p
                                    key={`${file.name}-${i}`}
                                    className="text-danger small mb-1"
                                    title={file.name}
                                    style={{
                                      wordBreak: "break-word",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {file.name}
                                  </p>
                                ))}
                              </>
                            ) : existing.length > 0 ? (
                              <>
                                {/* Previously Uploaded Files */}
                                <i
                                  className="bi bi-file-earmark-pdf-fill text-danger"
                                  style={{ fontSize: "48px" }}
                                />

                                {existing.map((file, i) => (
                                  <a
                                    key={file.id ?? i}
                                    href={
                                      file.url?.startsWith("http")
                                        ? file.url
                                        : `${apibaseurl}${file.url}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="d-block text-danger text-decoration-none small mb-1"
                                    style={{
                                      wordBreak: "break-word",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {file.original_name ||
                                      file.name ||
                                      `Document ${i + 1}`}
                                  </a>
                                ))}
                              </>
                            ) : (
                              <>
                                {/* No File Selected */}
                                <i
                                  className="bi bi-file-earmark-pdf-fill text-danger"
                                  style={{ fontSize: "48px" }}
                                />

                                <p className="text-muted small mb-0">
                                  No File Selected
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const LastTwoEducationForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { authData } = useAuth();
  const templates = useSelector((s: RootState) => s.package.templates);
  const template = templates.find(
    (t: any) => t.check_name === "2 Highest Education",
  );
  const assignmentTemplateId =
    state?.assignment_template_id || template?.assignment_template_id;
  const templateStatus = template?.status;

  const isDisabled = disabledStatuses.includes(templateStatus || "");
  const [educations, setEducations] = useState<Entry[]>(() => [emptyEntry()]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);
  const saveAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
    { method: "POST", autoCall: false, handleGlobalError: false },
  );
  const updateAPI = useApi<any>(
    `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
    { method: "PUT", autoCall: false, handleGlobalError: false },
  );
  const replyAPI = useApi<any>("/api/v1/insufficiency/candidate-response", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });
  const formFieldsAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  // Employment-style load: retrieve the full saved array once when page opens.
  const educationForm = useCallback(async () => {
    if (!assignmentTemplateId) {
      setInitialLoading(false);
      return;
    }
    setInitialLoading(true);
    try {
      const response = await formFieldsAPI.callApi();
      if (response?.success) {
        const restored = restoredEntries(response);
        setEducations(restored?.length ? restored : [emptyEntry()]);
      } else {
        setEducations([emptyEntry()]);
      }
    } catch (error) {
      console.error("Education form load error:", error);
      setEducations([emptyEntry()]);
    } finally {
      setInitialLoading(false);
    }
    // Run on template change, not every useApi render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentTemplateId]);

  useEffect(() => {
    void educationForm();
  }, [educationForm]);

  const patchEntry = useCallback((id: number, patch: Partial<Entry>) => {
    setEducations((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    );
  }, []);
  const changeField = useCallback((id: number, fieldId: string, value: any) => {
    setEducations((prev) =>
      prev.map((e) =>
        e.id === id
          ? {
              ...e,
              values: { ...e.values, [fieldId]: value },
              errors: { ...e.errors, [fieldId]: "" },
            }
          : e,
      ),
    );
  }, []);
  const chooseType = useCallback((id: number, type: string) => {
    setEducations((prev) =>
      prev.map((e) =>
        e.id === id
          ? {
              ...e,
              education_type: type,
              values: {},
              fields: [],
              uploadedFiles: {},
              noDocument: {},
              errors: {},
              loaded: false,
              loading: false,
              formTypeNew: undefined,
            }
          : e,
      ),
    );
  }, []);
  const removeEntry = async (id: number) => {
    const result = await Swal.fire({
      title: "Remove education?",
      text: "This will remove the unsaved education details from the form.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d92d20",
    });
    if (result.isConfirmed)
      setEducations((prev) =>
        prev.length > 1 ? prev.filter((e) => e.id !== id) : prev,
      );
  };

  const validate = () => {
    let invalid = false;
    const next = educations.map((entry) => {
      const errors: Record<string, string> = {};
      if (!entry.education_type)
        errors.education_type = "Select a qualification";
      if (entry.education_type && !entry.loaded)
        errors.education_type = "Qualification fields are not loaded";
      const shape: Record<string, z.ZodTypeAny> = {};
      entry.fields.forEach((field) => {
        if (field.type === "file") {
          const exists = (entry.uploadedFiles[field.field_id] || []).length > 0;
          shape[field.field_id] =
            field.required && !entry.noDocument[field.field_id] && !exists
              ? z
                  .any()
                  .refine(
                    (v) => fileArray(v).length > 0,
                    `${field.label} is required`,
                  )
              : z.any().optional();
        } else {
          let schema = z.string();
          if (field.required)
            schema = schema.min(1, `${field.label} is required`);
          if (field.validation_rules?.minLength)
            schema = schema.min(
              field.validation_rules.minLength,
              `${field.label} must have at least ${field.validation_rules.minLength} characters`,
            );
          if (field.validation_rules?.maxLength)
            schema = schema.max(
              field.validation_rules.maxLength,
              `${field.label} must have at most ${field.validation_rules.maxLength} characters`,
            );
          const regex =
            field.validation_rules?.regex || field.validation_rules?.pattern;
          if (regex) {
            try {
              schema = schema.refine(
                (v) => !v || new RegExp(regex).test(v),
                `${field.label} is invalid`,
              ) as typeof schema;
            } catch {
              /* Invalid backend regex should not crash the form. */
            }
          }
          shape[field.field_id] = schema;
        }
      });
      const result = z.object(shape).safeParse(entry.values);
      if (!result.success)
        result.error.issues.forEach((issue) => {
          const key = String(issue.path[0]);
          if (!errors[key]) errors[key] = issue.message;
        });
      if (Object.keys(errors).length) invalid = true;
      return {
        ...entry,
        errors,
        collapsed: Object.keys(errors).length ? false : entry.collapsed,
      };
    });
    setEducations(next);
    if (invalid)
      Swal.fire(
        "Incomplete details",
        "Please complete the required fields in every education section.",
        "warning",
      );
    return !invalid;
  };

  // Same multipart contract as Employment: JSON array without File objects,
  // binary files indexed by education row and dynamic field id.
  const buildPayload = () => {
    const payload = new FormData();
    const data = educations.map((entry) => {
      const record: Values = { education_type: entry.education_type };
      Object.entries(entry.values).forEach(([key, value]) => {
        if (
          value instanceof File ||
          (Array.isArray(value) && value.some((v) => v instanceof File))
        )
          return;
        record[key] = value ?? "";
      });
      return record;
    });
    educations.forEach((entry, index) => {
      Object.entries(entry.values).forEach(([fieldId, value]) => {
        if (entry.noDocument[fieldId]) return;
        fileArray(value).forEach((file) =>
          payload.append(`education_${index}_${fieldId}`, file),
        );
      });
    });
    payload.append("formData", JSON.stringify(data));
    payload.append("form_type", educations[0]?.formTypeNew || "education");
    // console.log(payload, "payloadpayloadpayload");
    // return;
    return payload;
  };
  const submit = async (isUpdate: boolean) => {
    if (!validate()) return;
    try {
      const response = await (isUpdate ? updateAPI : saveAPI).callApi(
        buildPayload(),
      );
      if (response?.success) {
        await Swal.fire(
          "Success",
          response.message || "Education details saved successfully.",
          "success",
        );
        dispatch(triggerPackageReload());
        educationForm();
      } else {
        Swal.fire(
          "Save failed",
          response?.message || "Unable to save education details.",
          "error",
        );
      }
    } catch (err: any) {
      Swal.fire(
        "Save failed",
        err?.response?.data?.error || err?.message || "Something went wrong.",
        "error",
      );
    }
  };
  const submitReply = async () => {
    if (!replyRemarks.trim()) {
      Swal.fire("Remarks required", "Please enter remarks.", "warning");
      return;
    }
    const payload = new FormData();
    payload.append(
      "assignment_template_id",
      String(state?.assignment_template_id || assignmentTemplateId || ""),
    );
    payload.append(
      "assignment_id",
      String(state?.assignment_id || template?.assignment_id || ""),
    );
    payload.append(
      "case_id",
      String(state?.case_id || template?.case_id || ""),
    );
    payload.append(
      "candidate_id",
      String(state?.candidate_id || authData?.candidate?.candidateId || ""),
    );
    payload.append("actor_due_on", state?.actor_type || "CANDIDATE");
    payload.append("remarks", replyRemarks);
    replyDocuments.forEach((f) => payload.append("documents", f));
    try {
      const res = await replyAPI.callApi(payload);
      if (res?.success) {
        await Swal.fire(
          "Success",
          "Response submitted successfully.",
          "success",
        );
        navigate(all_routes.caseSummary);
      } else
        Swal.fire(
          "Submission failed",
          res?.message || "Unable to submit response.",
          "error",
        );
    } catch (err: any) {
      Swal.fire(
        "Submission failed",
        err?.message || "Something went wrong.",
        "error",
      );
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-lg-8">
            <div className="card p-4">
              <div className="mb-4">
                {/* Section Header */}
                <div className="d-flex align-items-start gap-3 pb-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                    style={{
                      width: "44px",
                      height: "44px",
                      backgroundColor: "#EFF6FF",
                      color: "#2563EB",
                    }}
                  >
                    <i className="bi bi-mortarboard fs-3"></i>
                  </div>

                  <div>
                    <h5
                      className="fw-semibold mb-1"
                      style={{ color: "#101828", fontSize: "16px" }}
                    >
                      Two Highest Education Qualifications
                    </h5>

                    <p
                      className="mb-0"
                      style={{
                        color: "#667085",
                        fontSize: "13px",
                        lineHeight: "21px",
                      }}
                    >
                      Start with your highest qualification, then add your
                      second-highest education record.
                    </p>
                  </div>
                </div>

                {/* Section Divider */}
                <div
                  style={{
                    height: "1px",
                    backgroundColor: "#EAECF0",
                    width: "100%",
                  }}
                />

                {/* Template Error */}
                {!assignmentTemplateId && (
                  <div
                    className="alert alert-danger d-flex align-items-center gap-2 mt-3 mb-0"
                    role="alert"
                  >
                    <i className="bi bi-exclamation-circle fs-5"></i>
                    <span>
                      Education assignment template could not be found.
                    </span>
                  </div>
                )}
              </div>

              {initialLoading && (
                <div className="text-center py-4">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading education records...
                </div>
              )}
              {assignmentTemplateId &&
                !initialLoading &&
                educations.map((entry, index) => (
                  <EducationCard
                    key={entry.id}
                    entry={entry}
                    index={index}
                    assignmentTemplateId={assignmentTemplateId}
                    disabled={
                      isDisabled || saveAPI.loading || updateAPI.loading
                    }
                    usedTypes={educations
                      .map((e) => e.education_type)
                      .filter(Boolean)}
                    canRemove={educations.length > 1}
                    onPatch={patchEntry}
                    onChange={changeField}
                    onChoose={chooseType}
                    onRemove={removeEntry}
                  />
                ))}
              {assignmentTemplateId &&
                !initialLoading &&
                educations.length < 2 &&
                !isDisabled && (
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100 mb-3"
                    onClick={() =>
                      setEducations((prev) => [...prev, emptyEntry()])
                    }
                  >
                    + Add More Education
                  </button>
                )}
              {assignmentTemplateId &&
                !initialLoading &&
                !isDisabled &&
                templateStatus !== "CANDIDATE_INSUFF" && (
                  <div className="d-flex gap-2 align-item-center">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => submit(false)}
                      disabled={
                        saveAPI.loading || educations.some((e) => e.loading)
                      }
                    >
                      {saveAPI.loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-device-floppy me-2"></i>
                          Save Education
                        </>
                      )}
                    </button>
                  </div>
                )}
              {assignmentTemplateId &&
                !initialLoading &&
                templateStatus === "CANDIDATE_INSUFF" && (
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={
                        updateAPI.loading || educations.some((e) => e.loading)
                      }
                      onClick={() => submit(true)}
                    >
                      {updateAPI.loading
                        ? "Updating..."
                        : "Save Form & Documents"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => setShowReplyModal(true)}
                    >
                      Reply to Insufficiency
                    </button>
                  </div>
                )}
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              {/* Header */}
              <div
                className="text-center py-4"
                style={{ backgroundColor: "#f7f1e3" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                  style={{ width: "70px", height: "70px" }}
                >
                  <i
                    className="bi bi-lightbulb-fill text-warning"
                    style={{ fontSize: "35px" }}
                  ></i>
                </div>

                <h6 className="fw-bold mb-0">Instructions</h6>
              </div>

              {/* Body */}
              <div className="card-body">
                {/* Instruction 1 */}
                <div className="d-flex mb-4">
                  <div
                    className="rounded me-3 mt-1 flex-shrink-0"
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: "#f4c542",
                    }}
                  ></div>

                  <div>
                    <h6 className="mb-1 fw-semibold">Instruction 1</h6>
                    <p className="text-muted mb-0 small">
                      Please provide your relevant education details in the
                      form, and ensure that the relevant document mentions your
                      name exactly as it appears on the official education
                      documents.
                    </p>
                  </div>
                </div>

                {/* Instruction 2 */}
                <div className="d-flex mb-4">
                  <div
                    className="rounded me-3 mt-1 flex-shrink-0"
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: "#f4c542",
                    }}
                  ></div>

                  <div>
                    <h6 className="mb-1 fw-semibold">Instruction 2</h6>

                    <p className="text-muted mb-0 small">
                      In case you changed your name later due to marriage/other
                      reason we have already captured that in the personal
                      informant section.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showReplyModal && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,.5)" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Respond to Insufficiency</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowReplyModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <label className="form-label">
                    Remarks <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control mb-3"
                    rows={4}
                    value={replyRemarks}
                    onChange={(e) => setReplyRemarks(e.target.value)}
                  />
                  <label className="form-label">Supporting Documents</label>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) =>
                      setReplyDocuments(Array.from(e.target.files || []))
                    }
                  />
                  {replyDocuments.map((f, i) => (
                    <div
                      className="d-flex justify-content-between mt-2"
                      key={`${f.name}-${i}`}
                    >
                      <span>{f.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          setReplyDocuments((prev) =>
                            prev.filter((_, j) => j !== i),
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <div className="modal-footer gap-2">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowReplyModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={replyAPI.loading}
                    onClick={submitReply}
                  >
                    {replyAPI.loading ? "Submitting..." : "Submit Response"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};
export default LastTwoEducationForm;
